import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  ClickAwayListener,
  Container,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  Paper,
  Stack,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  Heart,
  Menu,
  Search,
  ShoppingCart,
  Star,
  Store,
  User,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

function EcommerceB2B() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [isSticky, setIsSticky] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [wishlist, setWishlist] = useState([2, 4]);

  const searchRef = useRef(null);

  const brand = useMemo(
    () => ({
      name: "TradeNest",
      accent: "#0EA5E9",
      accent2: "#22C55E",
      bg0: "#F7FAFC",
      bg1: "#FFFFFF",
      card: "#FFFFFF",
      stroke: "rgba(2, 6, 23, 0.10)",
      text: "#0F172A",
      subtext: "rgba(15, 23, 42, 0.72)",
    }),
    []
  );

  const categories = [
    {
      name: "Industrial",
      image:
        "https://images.unsplash.com/photo-1581092580497-e0d23cbdf1dc?w=300&h=200&fit=crop",
      subcategories: ["Safety", "Tools", "Fasteners", "Electrical", "PPE"],
    },
    {
      name: "Office",
      image:
        "https://images.unsplash.com/photo-1520607162513-77705c0f0d4a?w=300&h=200&fit=crop",
      subcategories: ["Stationery", "Chairs", "Desks", "Printers", "Supplies"],
    },
    {
      name: "Electronics",
      image:
        "https://images.unsplash.com/photo-1498049794561-7780e7231661?w=300&h=200&fit=crop",
      subcategories: ["Laptops", "Networking", "Accessories", "Storage", "POS"],
    },
    {
      name: "Packaging",
      image:
        "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=300&h=200&fit=crop",
      subcategories: ["Boxes", "Labels", "Tape", "Wrap", "Pallets"],
    },
    {
      name: "Hospitality",
      image:
        "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=300&h=200&fit=crop",
      subcategories: ["Kitchen", "Cleaning", "Uniforms", "Consumables", "Linen"],
    },
    {
      name: "Construction",
      image:
        "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=300&h=200&fit=crop",
      subcategories: ["Cement", "Paint", "Plumbing", "Hardware", "Electrical"],
    },
  ];

  const products = [
    {
      id: 1,
      title: "Safety Helmet (Bulk Pack)",
      price: 150,
      discount: 18,
      rating: 4.6,
      moq: 10,
      lead: "2-3 days",
      image:
        "https://images.unsplash.com/photo-1603570419989-83d79d7e9eaa?w=300&h=300&fit=crop",
    },
    {
      id: 2,
      title: "Wi‑Fi Router (Business)",
      price: 200,
      discount: 12,
      rating: 4.2,
      moq: 5,
      lead: "1-2 days",
      image:
        "https://images.unsplash.com/photo-1606055854326-9b6a8843b1f0?w=300&h=300&fit=crop",
    },
    {
      id: 3,
      title: "Packaging Tape (Case)",
      price: 80,
      discount: 25,
      rating: 4.4,
      moq: 24,
      lead: "Same day",
      image:
        "https://images.unsplash.com/photo-1586528116493-92a1f0a4f8b0?w=300&h=300&fit=crop",
    },
    {
      id: 4,
      title: "Ergonomic Chair (Lot)",
      price: 250,
      discount: 15,
      rating: 4.0,
      moq: 4,
      lead: "3-5 days",
      image:
        "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=300&h=300&fit=crop",
    },
  ];

  const allProducts = [
    ...products.map((p) => ({ ...p, category: "B2B" })),
    {
      id: 5,
      title: "Thermal Labels (Roll)",
      price: 45,
      discount: 0,
      rating: 4.1,
      moq: 20,
      lead: "Same day",
      category: "Packaging",
      image:
        "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=300&h=300&fit=crop",
    },
    {
      id: 6,
      title: "Power Drill (Pro)",
      price: 180,
      discount: 10,
      rating: 4.5,
      moq: 3,
      lead: "2-3 days",
      category: "Industrial",
      image:
        "https://images.unsplash.com/photo-1586864387789-628af9feed72?w=300&h=300&fit=crop",
    },
  ];

  const slides = [
    "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=1400&h=520&fit=crop",
    "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1400&h=520&fit=crop",
    "https://images.unsplash.com/photo-1581092580497-e0d23cbdf1dc?w=1400&h=520&fit=crop",
    "https://images.unsplash.com/photo-1520607162513-77705c0f0d4a?w=1400&h=520&fit=crop",
    "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1400&h=520&fit=crop",
  ];

  useEffect(() => {
    const handleScroll = () => setIsSticky(window.scrollY > 100);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchQuery(query);

    if (!query.trim()) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const filtered = allProducts
      .filter(
        (product) =>
          product.title.toLowerCase().includes(query.toLowerCase()) ||
          (product.category || "").toLowerCase().includes(query.toLowerCase())
      )
      .slice(0, 6);
    setSearchSuggestions(filtered);
    setShowSuggestions(true);
  };

  const handleSearchSubmit = () => {
    if (searchQuery.trim()) {
      navigate(`/B2BShop?search=${encodeURIComponent(searchQuery)}`);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (product) => {
    navigate(`/B2BProduct?id=${product.id}`);
    setShowSuggestions(false);
    setSearchQuery("");
  };

  const handleClickAway = () => setShowSuggestions(false);

  const toggleWishlist = (productId) => {
    setWishlist((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  };

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () =>
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);

  return (
    <Box
      sx={{
        mt: 7,
        minHeight: "100vh",
        background: `radial-gradient(1200px 600px at 18% 0%, rgba(14,165,233,0.14) 0%, rgba(247,250,252,0) 58%),
                     radial-gradient(900px 520px at 88% 10%, rgba(34,197,94,0.12) 0%, rgba(247,250,252,0) 55%),
                     linear-gradient(180deg, ${brand.bg1} 0%, ${brand.bg0} 100%)`,
        color: brand.text,
      }}
    >
      <AppBar
        position={isSticky ? "fixed" : "static"}
        elevation={0}
        sx={{
          backdropFilter: "blur(10px)",
          bgcolor: "rgba(255,255,255,0.86)",
          borderBottom: `1px solid ${brand.stroke}`,
          transition: "all 0.25s",
          zIndex: 1100,
        }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ justifyContent: "space-between" }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  display: "grid",
                  placeItems: "center",
                  background: `linear-gradient(135deg, ${brand.accent} 0%, ${brand.accent2} 100%)`,
                }}
              >
                <Store size={20} color="#FFFFFF" />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1, color: brand.text }}>
                  {brand.name}
                </Typography>
                <Typography variant="caption" sx={{ color: brand.subtext }}>
                  B2B procurement • bulk pricing • invoices
                </Typography>
              </Box>
            </Stack>

            {!isMobile && (
              <Stack direction="row" spacing={1}>
                <Button
                  onClick={() => navigate("/EDashboard")}
                  sx={{ color: brand.text, textTransform: "none", fontWeight: 800 }}
                >
                  Dashboard
                </Button>
                <Button
                  onClick={() => navigate("/B2BShop")}
                  sx={{ color: brand.text, textTransform: "none", fontWeight: 800 }}
                >
                  Catalog
                </Button>
                <Button
                  onClick={() => navigate("/B2BRFQ")}
                  sx={{ color: brand.text, textTransform: "none", fontWeight: 800 }}
                >
                  RFQ
                </Button>
                <Button
                  onClick={() => navigate("/B2BContracts")}
                  sx={{ color: brand.text, textTransform: "none", fontWeight: 800 }}
                >
                  Contracts
                </Button>
              </Stack>
            )}

            <Stack direction="row" spacing={1} alignItems="center">
              <ClickAwayListener onClickAway={handleClickAway}>
                <Box sx={{ position: "relative" }}>
                  <TextField
                    ref={searchRef}
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onKeyDown={(e) => e.key === "Enter" && handleSearchSubmit()}
                    placeholder="Search SKU / category…"
                    size="small"
                    sx={{
                      width: isMobile ? 200 : 340,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 999,
                        bgcolor: "#FFFFFF",
                        color: brand.text,
                        border: `1px solid ${brand.stroke}`,
                        "& fieldset": { border: "none" },
                      },
                      "& .MuiOutlinedInput-input::placeholder": {
                        color: "rgba(15, 23, 42, 0.55)",
                        opacity: 1,
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <IconButton onClick={handleSearchSubmit} size="small">
                          <Search size={18} color={brand.text} />
                        </IconButton>
                      ),
                    }}
                  />

                  {showSuggestions && searchSuggestions.length > 0 && (
                    <Paper
                      sx={{
                        position: "absolute",
                        top: "110%",
                        left: 0,
                        right: 0,
                        zIndex: 2000,
                        borderRadius: 3,
                        overflow: "hidden",
                        bgcolor: "#FFFFFF",
                        border: `1px solid ${brand.stroke}`,
                      }}
                    >
                      <List dense>
                        {searchSuggestions.map((product) => (
                          <ListItem
                            key={product.id}
                            onClick={() => handleSuggestionClick(product)}
                            sx={{
                              cursor: "pointer",
                              "&:hover": { bgcolor: "rgba(255,255,255,0.05)" },
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1.5}
                              alignItems="center"
                              sx={{ width: "100%" }}
                            >
                              <Avatar
                                variant="rounded"
                                src={product.image}
                                sx={{ width: 36, height: 36 }}
                              />
                              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                sx={{ fontWeight: 800, color: brand.text }}
                                  noWrap
                                >
                                  {product.title}
                                </Typography>
                                <Typography
                                  variant="caption"
                                sx={{ color: brand.subtext }}
                                  noWrap
                                >
                                  {(product.category || "B2B") + " • ₹" + product.price}
                                </Typography>
                              </Box>
                            </Stack>
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  )}
                </Box>
              </ClickAwayListener>

              <IconButton onClick={() => navigate("/B2BCart")} sx={{ color: brand.text }}>
                <ShoppingCart />
              </IconButton>
              <IconButton onClick={() => navigate("/B2BUser")} sx={{ color: brand.text }}>
                <User />
              </IconButton>
              {isMobile && (
                <IconButton sx={{ color: brand.text }}>
                  <Menu />
                </IconButton>
              )}
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      <Box
        sx={{
          position: "relative",
          mt: isSticky ? "64px" : 0,
          height: { xs: 420, md: 520 },
          overflow: "hidden",
          borderBottom: `1px solid ${brand.stroke}`,
        }}
      >
        <Box
          component="img"
          src={slides[currentSlide]}
          alt="Hero"
          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(90deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.52) 55%, rgba(255,255,255,0.86) 100%)",
          }}
        />

        <Container
          maxWidth="lg"
          sx={{
            position: "absolute",
            inset: 0,
            display: "grid",
            alignItems: "center",
          }}
        >
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography
                variant={isMobile ? "h3" : "h2"}
                component={motion.div}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                sx={{ fontWeight: 950, letterSpacing: "-0.02em", color: brand.text }}
              >
                B2B eCommerce for fast procurement
              </Typography>
              <Typography
                variant="h6"
                component={motion.div}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                sx={{ mt: 2, color: brand.subtext, maxWidth: 640 }}
              >
                MOQ pricing, invoices, and vendor-ready catalog views — with a clean,
                modern dark theme.
              </Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={() => navigate("/B2BShop")}
                  sx={{
                    borderRadius: 999,
                    px: 3.5,
                    py: 1.3,
                    fontWeight: 900,
                    textTransform: "none",
                    color: "#FFFFFF",
                    background: `linear-gradient(135deg, ${brand.accent} 0%, ${brand.accent2} 100%)`,
                    "&:hover": {
                      background: `linear-gradient(135deg, ${brand.accent} 0%, ${brand.accent2} 100%)`,
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  Browse catalog
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate("/B2BRFQ")}
                  sx={{
                    borderRadius: 999,
                    px: 3.5,
                    py: 1.3,
                    fontWeight: 900,
                    textTransform: "none",
                    borderColor: brand.stroke,
                    color: brand.text,
                    "&:hover": { borderColor: "rgba(2, 6, 23, 0.20)", bgcolor: "rgba(2, 6, 23, 0.03)" },
                  }}
                >
                  Request a quote
                </Button>
              </Stack>

              <Stack direction="row" spacing={1} sx={{ mt: 3, flexWrap: "wrap" }}>
                <Chip label="GST-ready invoices" sx={{ bgcolor: brand.card, color: brand.text, border: `1px solid ${brand.stroke}` }} />
                <Chip label="MOQ / Bulk packs" sx={{ bgcolor: brand.card, color: brand.text, border: `1px solid ${brand.stroke}` }} />
                <Chip label="Lead time" sx={{ bgcolor: brand.card, color: brand.text, border: `1px solid ${brand.stroke}` }} />
              </Stack>
            </Grid>

            <Grid item xs={12} md={5}>
              <Box
                component={motion.div}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                sx={{
                  p: 2.5,
                  borderRadius: 4,
                  bgcolor: brand.card,
                  border: `1px solid ${brand.stroke}`,
                }}
              >
                <Typography variant="overline" sx={{ color: brand.subtext }}>
                  Quick actions
                </Typography>
                <Divider sx={{ my: 1.5, borderColor: brand.stroke }} />
                <Stack spacing={1.25}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => navigate("/B2BRFQ")}
                    sx={{
                      borderRadius: 2.5,
                      textTransform: "none",
                      fontWeight: 800,
                      borderColor: brand.stroke,
                      color: brand.text,
                      justifyContent: "space-between",
                      "&:hover": { bgcolor: "rgba(2, 6, 23, 0.03)", borderColor: "rgba(2, 6, 23, 0.20)" },
                    }}
                    endIcon={<ChevronRight size={18} />}
                  >
                    Upload requirement list
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => navigate("/B2BOrders")}
                    sx={{
                      borderRadius: 2.5,
                      textTransform: "none",
                      fontWeight: 800,
                      borderColor: brand.stroke,
                      color: brand.text,
                      justifyContent: "space-between",
                      "&:hover": { bgcolor: "rgba(2, 6, 23, 0.03)", borderColor: "rgba(2, 6, 23, 0.20)" },
                    }}
                    endIcon={<ChevronRight size={18} />}
                  >
                    Track orders & invoices
                  </Button>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Container>

        <IconButton
          onClick={prevSlide}
          sx={{
            position: "absolute",
            left: 16,
            top: "50%",
            translate: "0 -50%",
            color: brand.text,
            bgcolor: "rgba(255,255,255,0.85)",
            border: `1px solid ${brand.stroke}`,
            "&:hover": { bgcolor: "rgba(255,255,255,1)" },
          }}
        >
          <ChevronLeft />
        </IconButton>
        <IconButton
          onClick={nextSlide}
          sx={{
            position: "absolute",
            right: 16,
            top: "50%",
            translate: "0 -50%",
            color: brand.text,
            bgcolor: "rgba(255,255,255,0.85)",
            border: `1px solid ${brand.stroke}`,
            "&:hover": { bgcolor: "rgba(255,255,255,1)" },
          }}
        >
          <ChevronRight />
        </IconButton>
      </Box>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography variant="h4" sx={{ fontWeight: 900, mb: 3 }}>
          Browse categories
        </Typography>
        <Grid container spacing={3}>
          {categories.map((cat) => (
            <Grid key={cat.name} item xs={12} sm={6} md={4}>
              <Card
                component={motion.div}
                whileHover={{ y: -8 }}
                sx={{
                  cursor: "pointer",
                  borderRadius: 4,
                  overflow: "hidden",
                  bgcolor: brand.card,
                  border: `1px solid ${brand.stroke}`,
                }}
                onClick={() => navigate("/B2BShop")}
              >
                <Box sx={{ position: "relative" }}>
                  <CardMedia
                    component="img"
                    height="200"
                    image={cat.image}
                    alt={cat.name}
                    sx={{ opacity: 0.92 }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      background:
                        "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.75) 100%)",
                    }}
                  />
                  <Box sx={{ position: "absolute", left: 16, bottom: 12, right: 16 }}>
                    <Typography variant="h6" sx={{ fontWeight: 900 }}>
                      {cat.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.82)" }}>
                      {cat.subcategories.slice(0, 3).join(" • ")}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Box sx={{ py: 7, borderTop: `1px solid ${brand.stroke}` }}>
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 900 }}>
                Featured bulk deals
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                MOQ pricing and lead time shown on each item.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              sx={{
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 900,
                borderColor: brand.stroke,
                color: brand.text,
                "&:hover": { bgcolor: "rgba(2, 6, 23, 0.03)", borderColor: "rgba(2, 6, 23, 0.20)" },
              }}
              onClick={() => navigate("/B2BShop")}
            >
              View all
            </Button>
          </Stack>

          <Grid container spacing={3}>
            {products.map((product) => (
              <Grid key={product.id} item xs={12} sm={6} md={3}>
                <Card
                  component={motion.div}
                  whileHover={{ y: -10 }}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 4,
                    overflow: "hidden",
                    bgcolor: brand.card,
                    border: `1px solid ${brand.stroke}`,
                  }}
                  onClick={() => navigate(`/B2BProduct?id=${product.id}`)}
                >
                  <Box sx={{ position: "relative" }}>
                    <CardMedia
                      component="img"
                      height="230"
                      image={product.image}
                      alt={product.title}
                      sx={{ objectFit: "cover" }}
                    />
                    {!!product.discount && (
                      <Chip
                        label={`${product.discount}% OFF`}
                        size="small"
                        sx={{
                          position: "absolute",
                          top: 12,
                          left: 12,
                          bgcolor: "rgba(34,197,94,0.12)",
                          color: brand.text,
                          border: `1px solid rgba(34,197,94,0.35)`,
                          fontWeight: 900,
                        }}
                      />
                    )}
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleWishlist(product.id);
                      }}
                      sx={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        bgcolor: "rgba(255,255,255,0.9)",
                        border: `1px solid ${brand.stroke}`,
                        "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                      }}
                    >
                      <Heart
                        size={20}
                        fill={wishlist.includes(product.id) ? brand.accent : "none"}
                        color={wishlist.includes(product.id) ? brand.accent : brand.text}
                      />
                    </IconButton>
                  </Box>
                  <CardContent sx={{ p: 2.2, flexGrow: 1, display: "flex", flexDirection: "column" }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 0.75 }}>
                      {product.title}
                    </Typography>

                    <Stack direction="row" spacing={1} sx={{ mb: 1.25, flexWrap: "wrap" }}>
                      <Chip
                        size="small"
                        label={`MOQ ${product.moq}`}
                        sx={{ bgcolor: "rgba(14,165,233,0.10)", color: brand.text, border: `1px solid rgba(14,165,233,0.24)` }}
                      />
                      <Chip
                        size="small"
                        label={`Lead: ${product.lead}`}
                        sx={{ bgcolor: "#FFFFFF", color: brand.text, border: `1px solid ${brand.stroke}` }}
                      />
                    </Stack>

                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1.25 }}>
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          size={16}
                          fill={i < Math.floor(product.rating) ? "#F59E0B" : "none"}
                          color="#F59E0B"
                        />
                      ))}
                      <Typography variant="caption" sx={{ opacity: 0.8, ml: 0.5 }}>
                        {product.rating}
                      </Typography>
                    </Stack>

                    <Box sx={{ mt: "auto" }}>
                      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 1.25 }}>
                        <Typography variant="h5" sx={{ fontWeight: 950 }}>
                          ₹{Math.round(product.price - (product.price * product.discount) / 100)}
                        </Typography>
                        {!!product.discount && (
                          <Typography variant="body2" sx={{ opacity: 0.65, textDecoration: "line-through" }}>
                            ₹{product.price}
                          </Typography>
                        )}
                      </Stack>
                      <Button
                        fullWidth
                        variant="contained"
                        sx={{
                          borderRadius: 3,
                          textTransform: "none",
                          fontWeight: 950,
                          color: "#FFFFFF",
                          background: `linear-gradient(135deg, ${brand.accent} 0%, ${brand.accent2} 100%)`,
                          "&:hover": {
                            background: `linear-gradient(135deg, ${brand.accent} 0%, ${brand.accent2} 100%)`,
                            transform: "translateY(-1px)",
                          },
                        }}
                      >
                        Add to cart
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: 5, borderTop: `1px solid ${brand.stroke}` }}>
        <Container maxWidth="lg">
          <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 950 }}>
                {brand.name}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.75 }}>
                Built for procurement teams — fast search, bulk deals, and clean UI.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <IconButton sx={{ color: brand.text, border: `1px solid ${brand.stroke}` }}>
                <Typography variant="caption" sx={{ fontWeight: 900 }}>
                  FB
                </Typography>
              </IconButton>
              <IconButton sx={{ color: brand.text, border: `1px solid ${brand.stroke}` }}>
                <Typography variant="caption" sx={{ fontWeight: 900 }}>
                  IN
                </Typography>
              </IconButton>
              <IconButton sx={{ color: brand.text, border: `1px solid ${brand.stroke}` }}>
                <Typography variant="caption" sx={{ fontWeight: 900 }}>
                  X
                </Typography>
              </IconButton>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}

export default EcommerceB2B;

